import React, { useState } from 'react';
import Logo from '../assets/Logo.png';
import { login as loginApi } from '../lib/auth-api';
import { useAuthStore } from '../store/auth';

export function Login({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const setStatus = useAuthStore((s) => s.setStatus);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      setError('Please enter your email and password.');
      return;
    }

    setIsLoading(true);
    try {
      const user = await loginApi({ email: email.trim(), password });
      setStatus('authed');
      if (typeof onLogin === 'function') onLogin(user);
    } catch (err) {
      let msg;
      if (err?.code === 'UNAUTHORIZED') {
        msg = 'Incorrect email or password.';
      } else if (err?.code === 'RATE_LIMITED') {
        msg = 'Too many attempts. Try again in a moment.';
      } else if (err?.code === 'VALIDATION_FAILED') {
        // Surface the first field error so the user can self-diagnose
        const first = err?.details?.errors?.[0];
        const path = first?.loc?.slice(1).join('.') || 'field';
        msg = `Validation failed on ${path}: ${first?.msg || err.message}`;
      } else if (err?.code === 'NETWORK' || err?.status === 0) {
        msg = 'Cannot reach the API. Is the backend running on :8000?';
      } else {
        msg = err?.message || 'Sign-in failed. Please try again.';
      }
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={styles.root}>
      {/* Animated background orbs */}
      <div style={styles.orb1} />
      <div style={styles.orb2} />
      <div style={styles.orb3} />

      {/* Grid overlay */}
      <div style={styles.gridOverlay} />

      {/* Login card */}
      <div style={styles.card}>
        {/* Logo */}
        <div style={styles.logoWrapper}>
          <img src={Logo} alt="Akin Logo" style={styles.logo} />
        </div>

        <h1 style={styles.title}>Welcome Back</h1>
        <p style={styles.subtitle}>Sign in to your Akin Analytics account</p>

        {error && (
          <div style={styles.errorBox}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth="2" style={{ flexShrink: 0 }}>
              <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} style={styles.form} noValidate>
          {/* Email Field */}
          <div style={styles.fieldGroup}>
            <label htmlFor="login-email" style={styles.label}>Email Address</label>
            <div style={styles.inputWrapper}>
              <span style={styles.inputIcon}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
              </span>
              <input
                id="login-email"
                className="login-input"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={styles.input}
                autoComplete="email"
              />
            </div>
          </div>

          {/* Password Field */}
          <div style={styles.fieldGroup}>
            <label htmlFor="login-password" style={styles.label}>Password</label>
            <div style={styles.inputWrapper}>
              <span style={styles.inputIcon}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              </span>
              <input
                id="login-password"
                className="login-input"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{ ...styles.input, paddingRight: '48px' }}
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="login-eye-button"
                style={styles.eyeButton}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Forgot Password */}
          <div style={styles.forgotRow}>
            <button type="button" style={styles.forgotLink}></button>
          </div>

          {/* Submit Button */}
          <button
            id="login-submit-btn"
            type="submit"
            disabled={isLoading}
            style={{ ...styles.submitBtn, opacity: isLoading ? 0.8 : 1, cursor: isLoading ? 'not-allowed' : 'pointer' }}
          >
            {isLoading ? (
              <span style={styles.spinnerWrapper}>
                <span style={styles.spinner} />
                Signing in...
              </span>
            ) : (
              'Sign In'
            )}
          </button>
        </form>

        {/* Footer */}
        <p style={styles.footer}>
          {' '}
          <button type="button" style={styles.footerLink}></button>
        </p>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');

        @keyframes gradientShift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        @keyframes orbFloat1 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.05); }
          66% { transform: translate(-20px, 20px) scale(0.95); }
        }
        @keyframes orbFloat2 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(-40px, 30px) scale(1.08); }
          66% { transform: translate(25px, -40px) scale(0.92); }
        }
        @keyframes orbFloat3 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(20px, 40px) scale(1.06); }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes cardIn {
          from { opacity: 0; transform: translateY(28px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }

        .login-input {
          width: 100%;
          box-sizing: border-box;
          padding: 13px 14px 13px 44px;
          background: rgba(11, 15, 25, 0.72);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 14px;
          color: #f8fafc;
          font-size: 14px;
          font-family: 'Inter', sans-serif;
          outline: none;
          transition: border-color 0.2s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1), background 0.2s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: inset 0 2px 4px rgba(0,0,0,0.14);
          appearance: none;
          -webkit-appearance: none;
        }

        .login-input:hover {
          border-color: rgba(148, 163, 184, 0.28);
          background: rgba(13, 18, 31, 0.82);
        }

        .login-input:focus {
          border-color: rgba(99,102,241,0.72);
          background: rgba(11, 15, 25, 0.92);
          box-shadow: 0 0 0 4px rgba(99,102,241,0.14), inset 0 2px 4px rgba(0,0,0,0.18);
        }

        .login-input::placeholder {
          color: rgba(148,163,184,0.55);
        }

        .login-input:-webkit-autofill,
        .login-input:-webkit-autofill:hover,
        .login-input:-webkit-autofill:focus,
        .login-input:-webkit-autofill:active {
          -webkit-text-fill-color: #f8fafc;
          caret-color: #f8fafc;
          -webkit-box-shadow: 0 0 0 1000px rgba(11, 15, 25, 0.95) inset, 0 0 0 1px rgba(99,102,241,0.46);
          box-shadow: 0 0 0 1000px rgba(11, 15, 25, 0.95) inset, 0 0 0 1px rgba(99,102,241,0.46);
          border: 1px solid rgba(99,102,241,0.5);
          border-radius: 14px;
          transition: background-color 9999s ease-out 0s, color 9999s ease-out 0s;
        }

        .login-input:-webkit-autofill::first-line {
          font-family: 'Inter', sans-serif;
          font-size: 14px;
        }

        .login-eye-button:hover {
          color: rgba(226,232,240,0.92);
        }

        .login-eye-button:focus-visible {
          outline: none;
          box-shadow: 0 0 0 3px rgba(99,102,241,0.18);
        }
      `}</style>
    </div>
  );
}

const styles = {
  root: {
    position: 'fixed',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #080b14 0%, #0d1225 40%, #0a0f1e 100%)',
    fontFamily: "'Inter', sans-serif",
    overflow: 'hidden',
    zIndex: 9999,
  },
  orb1: {
    position: 'absolute',
    width: '520px',
    height: '520px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(99,102,241,0.18) 0%, transparent 70%)',
    top: '-100px',
    left: '-80px',
    animation: 'orbFloat1 12s ease-in-out infinite',
    pointerEvents: 'none',
  },
  orb2: {
    position: 'absolute',
    width: '600px',
    height: '600px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(16,185,129,0.12) 0%, transparent 70%)',
    bottom: '-150px',
    right: '-100px',
    animation: 'orbFloat2 15s ease-in-out infinite',
    pointerEvents: 'none',
  },
  orb3: {
    position: 'absolute',
    width: '350px',
    height: '350px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(139,92,246,0.12) 0%, transparent 70%)',
    top: '50%',
    left: '60%',
    animation: 'orbFloat3 10s ease-in-out infinite',
    pointerEvents: 'none',
  },
  gridOverlay: {
    position: 'absolute',
    inset: 0,
    backgroundImage: 'linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)',
    backgroundSize: '48px 48px',
    pointerEvents: 'none',
  },
  card: {
    position: 'relative',
    zIndex: 1,
    width: '100%',
    maxWidth: '420px',
    margin: '0 16px',
    padding: '44px 40px',
    background: 'rgba(15, 20, 40, 0.75)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '24px',
    backdropFilter: 'blur(24px)',
    WebkitBackdropFilter: 'blur(24px)',
    boxShadow: '0 32px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04) inset',
    animation: 'cardIn 0.55s cubic-bezier(0.22, 1, 0.36, 1) both',
  },
  logoWrapper: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '28px',
  },
  logo: {
    height: '52px',
    width: 'auto',
    objectFit: 'contain',
    filter: 'drop-shadow(0 0 12px rgba(99,102,241,0.35))',
  },
  title: {
    margin: '0 0 6px',
    fontSize: '26px',
    fontWeight: '700',
    color: '#f1f5f9',
    textAlign: 'center',
    letterSpacing: '-0.5px',
  },
  subtitle: {
    margin: '0 0 28px',
    fontSize: '14px',
    color: 'rgba(148,163,184,0.8)',
    textAlign: 'center',
    fontWeight: '400',
  },
  errorBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 14px',
    marginBottom: '20px',
    background: 'rgba(239,68,68,0.1)',
    border: '1px solid rgba(239,68,68,0.25)',
    borderRadius: '10px',
    color: '#f87171',
    fontSize: '13px',
    fontWeight: '500',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  fieldGroup: {
    marginBottom: '18px',
  },
  label: {
    display: 'block',
    marginBottom: '7px',
    fontSize: '13px',
    fontWeight: '500',
    color: 'rgba(203,213,225,0.85)',
    letterSpacing: '0.01em',
  },
  inputWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  inputIcon: {
    position: 'absolute',
    left: '14px',
    color: 'rgba(148,163,184,0.6)',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
  },
  input: {
    width: '100%',
    boxSizing: 'border-box',
  },
  eyeButton: {
    position: 'absolute',
    right: '12px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: 'rgba(148,163,184,0.6)',
    display: 'flex',
    alignItems: 'center',
    padding: '4px',
    borderRadius: '6px',
    transition: 'color 0.2s',
  },
  forgotRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: '24px',
    marginTop: '-4px',
  },
  forgotLink: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '12.5px',
    color: 'rgba(99,102,241,0.9)',
    fontFamily: "'Inter', sans-serif",
    fontWeight: '500',
    padding: 0,
    transition: 'color 0.2s',
  },
  submitBtn: {
    width: '100%',
    padding: '13px',
    background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
    border: 'none',
    borderRadius: '12px',
    color: '#fff',
    fontSize: '15px',
    fontWeight: '600',
    fontFamily: "'Inter', sans-serif",
    letterSpacing: '0.01em',
    cursor: 'pointer',
    transition: 'opacity 0.2s, transform 0.15s, box-shadow 0.2s',
    boxShadow: '0 4px 24px rgba(99,102,241,0.35)',
    marginBottom: '4px',
  },
  spinnerWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
  },
  spinner: {
    display: 'inline-block',
    width: '16px',
    height: '16px',
    border: '2.5px solid rgba(255,255,255,0.3)',
    borderTop: '2.5px solid #fff',
    borderRadius: '50%',
    animation: 'spin 0.75s linear infinite',
  },
  footer: {
    marginTop: '24px',
    fontSize: '13px',
    color: 'rgba(148,163,184,0.7)',
    textAlign: 'center',
    marginBottom: 0,
  },
  footerLink: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: 'rgba(99,102,241,0.9)',
    fontFamily: "'Inter', sans-serif",
    fontSize: '13px',
    fontWeight: '500',
    padding: 0,
    transition: 'color 0.2s',
  },
};
